(() => {
  const drawer = document.getElementById('bb-drawer');
  const drawerOverlay = document.getElementById('bb-overlay');
  const drawerClose = document.getElementById('bb-dx');
  const drawerOpeners = [
    document.getElementById('bb-ham'),
    document.getElementById('allbtn'),
  ].filter(Boolean);
  const emirateSheet = document.getElementById('bb-emirate-sheet');
  const emirateOverlay = document.getElementById('bb-emirate-overlay');
  const emirateClose = document.getElementById('bb-emirate-close');
  const emirateTriggers = [
    document.getElementById('bb-loc-trigger'),
    document.getElementById('bb-deliver-trigger'),
  ].filter(Boolean);
  let drawerOpener = null;
  let emirateOpener = null;

  const getFocusable = (container) =>
    Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hidden && element.getClientRects().length > 0);

  const setDrawerState = (open, restoreFocus = true) => {
    if (!drawer) return;
    drawer.classList.toggle('bb-open', open);
    drawerOverlay?.classList.toggle('bb-open', open);
    drawer.toggleAttribute('inert', !open);
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    drawerOpeners.forEach((button) => button.setAttribute('aria-expanded', open ? 'true' : 'false'));
    if (open) {
      requestAnimationFrame(() => drawerClose?.focus());
    } else if (restoreFocus && drawerOpener?.isConnected) {
      requestAnimationFrame(() => drawerOpener.focus());
    }
  };

  const setEmirateState = (open, restoreFocus = true) => {
    if (!emirateSheet) return;
    emirateSheet.classList.toggle('bb-open', open);
    emirateOverlay?.classList.toggle('bb-open', open);
    emirateSheet.toggleAttribute('inert', !open);
    emirateSheet.setAttribute('aria-hidden', open ? 'false' : 'true');
    emirateTriggers.forEach((button) => button.setAttribute('aria-expanded', open ? 'true' : 'false'));
    if (open) {
      requestAnimationFrame(() => {
        const selected = emirateSheet.querySelector('[role="radio"][aria-checked="true"]');
        (selected || emirateClose)?.focus();
      });
    } else if (restoreFocus && emirateOpener?.isConnected) {
      requestAnimationFrame(() => emirateOpener.focus());
    }
  };

  drawerOpeners.forEach((button) => {
    button.setAttribute('aria-expanded', 'false');
    button.addEventListener('click', () => {
      drawerOpener = button;
      setDrawerState(true, false);
    });
  });

  drawerClose?.addEventListener('click', () => setDrawerState(false));
  drawerOverlay?.addEventListener('click', () => setDrawerState(false));

  emirateTriggers.forEach((button) => {
    button.setAttribute('aria-expanded', 'false');
    button.addEventListener('click', () => {
      emirateOpener = button;
      setEmirateState(true, false);
    });
  });

  emirateClose?.addEventListener('click', () => setEmirateState(false));
  emirateOverlay?.addEventListener('click', () => setEmirateState(false));
  emirateSheet?.querySelectorAll('.bb-emirate-opt').forEach((option) => {
    option.addEventListener('click', () => setEmirateState(false));
  });

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Escape') {
        if (drawer?.classList.contains('bb-open')) {
          event.preventDefault();
          setDrawerState(false);
          return;
        }
        if (emirateSheet?.classList.contains('bb-open')) {
          event.preventDefault();
          setEmirateState(false);
        }
        return;
      }

      if (event.key !== 'Tab') return;
      const activeDialog = drawer?.classList.contains('bb-open')
        ? drawer
        : emirateSheet?.classList.contains('bb-open')
          ? emirateSheet
          : null;
      if (!activeDialog) return;
      const focusable = getFocusable(activeDialog);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    true
  );

  /* ---------- sticky offset for the collection rail (27 Aug 2026) ----------
     bb-coffee.js keeps --bbc-sticky-top (the offset the filter rail and the
     quick-tab pills pin to) in step with the header's bottom edge, and listens
     for it on window scroll. On desktop Horizon does not scroll the window:
     base.css gives .page-wrapper height:100dvh and overflow-y:auto and holds
     html/body at overflow:hidden, so the page scrolls inside that div. Scroll
     events do not bubble, so that window listener never fired once — the
     variable kept the value it was given at load, measured while the 47px
     announcement bar was still on screen, and the rail pinned 47px too low
     against a header that had already scrolled away.

     Capture phase still sees a scroll from any element, so this one listener
     covers the desktop .page-wrapper scroller and the mobile viewport without
     naming either. Measured off .bb-header-section, which is what carries the
     sticky since the header fix in bb-ux-fixes.css; #bb-hd is the fallback and
     gives the same bottom edge. Kept here with the other header corrections —
     fold it into bb-coffee.js's own sticky-offset block if that file is ever
     opened for another reason. */
  let syncRailOffset = () => {};
  let syncRailSettle = () => {};
  const stickyHost = document.querySelector('.bb-header-section') || document.getElementById('bb-hd');
  if (stickyHost) {
    let stickyPending = false;
    /* 28 Aug 2026 (scroll-perf): writing a custom property on <html> invalidates
       style for the whole tree, and this ran on every scroll frame even though
       the value only actually changes near the top of the page and when the
       fold toggles. Skip the write when the offset is unchanged - the reads
       that remain are cheap on a clean layout. bb-coffee.js's own sticky loop
       now bows out when this header exists (same date stamp there), so nothing
       fights this value any more. */
    let lastStickyOffset = null;
    const syncStickyTop = () => {
      stickyPending = false;
      const position = getComputedStyle(stickyHost).position;
      const offset = position === 'sticky' || position === 'fixed'
        ? Math.max(0, Math.round(stickyHost.getBoundingClientRect().bottom))
        : 0;
      if (offset === lastStickyOffset) return;
      lastStickyOffset = offset;
      document.documentElement.style.setProperty('--bbc-sticky-top', offset + 'px');
    };
    const queueStickyTop = () => {
      if (stickyPending) return;
      stickyPending = true;
      requestAnimationFrame(syncStickyTop);
    };
    /* the fold animates the header for ~340ms; a lone measurement taken as the
       toggle fires would go stale the moment the reader's finger stops. This
       bounded loop tracks the transition and then stops - it runs only on fold
       toggles, never during plain scrolling (48 frames covers the transition
       on 60Hz and 120Hz screens; the change-check above makes the quiet frames
       read-only). */
    let settleFrames = 0;
    const settleLoop = () => {
      syncStickyTop();
      if (settleFrames > 0) { settleFrames--; requestAnimationFrame(settleLoop); }
    };
    syncRailSettle = (frames) => {
      const running = settleFrames > 0;
      settleFrames = Math.max(settleFrames, frames);
      if (!running) requestAnimationFrame(settleLoop);
    };
    document.addEventListener('scroll', queueStickyTop, { capture: true, passive: true });
    window.addEventListener('resize', queueStickyTop, { passive: true });
    syncRailOffset = queueStickyTop;
    queueStickyTop();
  }

  /* ---------- rail eyebrow + drawer reset (27 Aug 2026) ----------
     Two small pieces of mockup parity that bb-collection-main.liquid does not
     render today: the rail's "Make it yours" eyebrow, and the Reset that sits
     beside "Show N results" in the filter drawer's foot. Both are decorative
     or navigational rather than content — the rail's own "Refine" label and
     the server-rendered "Clear all" pill above the grid are untouched — so
     they go here instead of retyping a 32KB section for two lines. Move them
     into the section's markup whenever that file is next opened for another
     reason. */
  const addRailEyebrow = () => {
    const head = document.querySelector('#desktopFilters .rail-head');
    if (!head || head.querySelector('.eyebrow')) return;
    const eyebrow = document.createElement('p');
    eyebrow.className = 'eyebrow';
    eyebrow.textContent = 'Make it yours';
    head.insertBefore(eyebrow, head.firstChild);
  };
  addRailEyebrow();

  const rail = document.getElementById('desktopFilters');
  /* the coffee-finder quiz rewrites #desktopFilters wholesale when it applies a
     match, which drops anything added here — put it back when that happens */
  if (rail && 'MutationObserver' in window) {
    new MutationObserver(addRailEyebrow).observe(rail, { childList: true, subtree: true });
  }

  const filterFoot = document.querySelector('#bbc-filterDrawer .drawer-foot');
  if (filterFoot && !filterFoot.querySelector('.btn-reset')) {
    const apply = filterFoot.querySelector('.btn-primary');
    if (apply) apply.style.removeProperty('width');
    const reset = document.createElement('a');
    reset.className = 'btn btn-reset';
    const app = document.getElementById('bbc-app');
    reset.href = (app && app.getAttribute('data-bbc-collection-url')) || '/collections/coffee-beans';
    reset.textContent = 'Reset';
    filterFoot.insertBefore(reset, filterFoot.firstChild);
  }

  /* ---------- mobile header fold (28 Aug 2026, v2) ----------
     Position-based, one-way-per-direction: past 90px the header folds to the
     search field alone; it returns only under 30px. Direction of scroll no
     longer matters - Salem, phone test: "When I slightly scroll up while I'm
     in the middle of the screen, everything shouldn't appear. Everything
     only appears when I go all the way to the top."

     This replaces the is-condensed machinery (delta tracking, the fold lock,
     the injected search icon). A position test needs none of it: the gap
     between the two thresholds absorbs the content shift the fold itself
     causes, so there is no feedback loop to suppress. CSS for the fold lives
     in bb-ux-fixes.css under the same date stamp. */
  const foldHeader = document.getElementById('bb-hd');
  if (foldHeader) {
    const compact = window.matchMedia('(max-width: 1120px)');
    const searchField = foldHeader.querySelector('.bb-search input[type="search"]');
    let folded = false;

    /* 28 Aug 2026 (scroll-perf): the wrapper is rendered once by theme.liquid
       and never replaced, so look it up once instead of running querySelector
       on every scroll event of the page's life */
    const foldWrapper = document.querySelector('.page-wrapper');
    const foldScrollTop = () => {
      if (foldWrapper && foldWrapper.scrollHeight > foldWrapper.clientHeight + 4) return foldWrapper.scrollTop;
      return window.scrollY || document.documentElement.scrollTop || 0;
    };

    const setFolded = (on) => {
      if (on === folded) return;
      folded = on;
      foldHeader.classList.toggle('is-scrolled', on);
      /* the header animates its height for ~340ms - track it to the end so the
         rail and pills land flush (see syncRailSettle above) */
      syncRailSettle(48);
    };

    const onFoldScroll = () => {
      if (!compact.matches) { setFolded(false); return; }
      /* a drawer locks the body and parks it at a negative top - that jump is
         not a reader scrolling, so ignore it */
      if (document.body.classList.contains('bbc-locked')) return;
      /* while someone is typing in search, the iOS keyboard resizes and
         scrolls the page - do not fold the field they are typing into */
      if (searchField && document.activeElement === searchField) return;
      const y = foldScrollTop();
      if (y > 90) setFolded(true);
      else if (y < 30) setFolded(false);
    };

    document.addEventListener('scroll', onFoldScroll, { capture: true, passive: true });
    if (compact.addEventListener) compact.addEventListener('change', onFoldScroll);
    onFoldScroll();
  }

  /* ---------- Bean Stamps bar: tap tracking (27 Aug 2026) ----------
     The bar is an awareness surface, not a status widget: the balance comes
     from customer.metafields.bean_stamps.balance, written by the app after an
     order, so a signed-out visitor sees a promise rather than a number. The
     point of it is that a new visitor gets curious and taps.

     Nothing measured that. bean-stamps.js has no gtag call at all - the one
     bb_stamp_* string in it, bb_stamp_ring_seen, is a sessionStorage key that
     suppresses a repeat of the ring animation, not an event. That made the bar
     the only interactive surface on the page with no event on it, next to
     bb_circle_tap, bb_xsell_tap, bb_quiz_open, bb_fav_toggle, bb_load_more and
     select_item. Traffic to /pages/bean-stamps would show up in GA4 but there
     would be no way to separate the header bar from every other route in.

     signed_in and balance are read from the server-rendered state block, so a
     curious new visitor and a returning customer checking their count are two
     different rows rather than one number. No preventDefault: the anchor
     navigates as it always did and gtag flushes on its own transport. */
  const stampBar = document.querySelector('.bstamp-bar');
  if (stampBar) {
    let stampState = {};
    try {
      const stateNode = document.getElementById('bean-stamps-state');
      if (stateNode) stampState = JSON.parse(stateNode.textContent) || {};
    } catch (e) {
      stampState = {};
    }
    stampBar.addEventListener('click', () => {
      try {
        if (window.gtag) {
          window.gtag('event', 'bb_stamp_bar_tap', {
            signed_in: stampState.signedIn === true,
            balance: Number(stampState.balance) || 0,
          });
        }
      } catch (e) {}
    });
  }

  setDrawerState(false, false);
  setEmirateState(false, false);
})();
